import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import Layout from '../Layout/Layout';

const Container = styled.div`
  padding: 32px;
  max-width: 1000px;
  margin: 0 auto;
  animation: fadeIn 0.5s ease-out;

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const Form = styled.form`
  background: var(--surface);
  padding: 40px;
  border-radius: 24px;
  box-shadow: var(--shadow-lg);
  border: 1px solid var(--border);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: var(--gradient-primary);
  }
`;

const OrderSummary = styled.div`
  background: var(--surface);
  padding: 32px;
  border-radius: 24px;
  box-shadow: var(--shadow-lg);
  margin-bottom: 32px;
  border: 1px solid var(--border);
  
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SummaryItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  label {
    color: var(--text-secondary);
    font-size: 14px;
  }

  span {
    font-size: 18px;
    font-weight: 600;
    color: var(--text-primary);
  }
`;

const Title = styled.h2`
  font-size: 32px;
  margin-bottom: 32px;
  background: var(--gradient-primary);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 800;
`;

const FormGroup = styled.div`
  margin-bottom: 24px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  color: var(--text-secondary);
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 16px;
  border: 1px solid var(--border);
  border-radius: 12px;
  font-size: 16px;
  background: var(--surface);
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px var(--primary-light);
  }

  &::placeholder {
    color: var(--text-secondary);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

const PayButton = styled.button`
  flex: 1;
  padding: 16px;
  background: var(--text-primary);
  color: var(--surface);
  border: none;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const CancelButton = styled.button`
  flex: 1;
  padding: 16px;
  background: var(--surface);
  color: var(--text-primary);
  border: 1px solid var(--border);
  border-radius: 6px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background: var(--hover);
  }
`;

const PaymentConfirmation = styled.div`
  margin-top: 32px;
  padding: 32px;
  background: var(--surface);
  border: 1px solid var(--success);
  border-radius: 24px;
  box-shadow: var(--shadow-lg);
  animation: slideUp 0.5s ease-out;

  @keyframes slideUp {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const PaymentSummary = styled.div`
  margin-bottom: 24px;
  padding-bottom: 24px;
  border-bottom: 1px solid var(--border);

  h3 {
    font-size: 24px;
    margin-bottom: 16px;
    color: var(--text-primary);
    font-weight: 700;
  }

  p {
    display: flex;
    justify-content: space-between;
    margin: 12px 0;
    font-size: 16px;
    color: var(--text-secondary);

    span:last-child {
      font-weight: 600;
      color: var(--text-primary);
    }
  }
`;

const ConfirmButton = styled.button`
  width: 100%;
  padding: 16px;
  border-radius: 12px;
  border: none;
  background: var(--success);
  color: white;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: all var(--transition-normal);
  margin-top: 16px;
  box-shadow: var(--shadow-md);

  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-lg);
  }

  &:disabled {
    background: var(--disabled);
    cursor: not-allowed;
    transform: none;
  }
`;

const ErrorMessage = styled.div`
  color: var(--error);
  background: ${props => `${props.theme.error}10`};
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;

  &::before {
    content: '⚠️';
  }
`;

const PaymentOptions = styled.div`
  margin-bottom: 32px;
`;

const PayPalButton = styled.button`
  width: 100%;
  padding: 16px;
  border-radius: 12px;
  border: 1px solid var(--border);
  background: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  transition: all 0.3s ease;

  img {
    height: 24px;
  }

  &:hover {
    background: var(--surface-hover);
    transform: translateY(-2px);
    box-shadow: var(--shadow-md);
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  text-align: center;
  margin: 24px 0;
  color: var(--text-secondary);

  &::before,
  &::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid var(--border);
  }

  span {
    margin: 0 16px;
    font-size: 14px;
  }
`;

const CardIcons = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
`;

const InputWrapper = styled.div`
  position: relative;
`;

const SubscribeButton = styled.button`
  width: 100%;
  padding: 16px;
  border-radius: 12px;
  border: none;
  background: var(--success);
  color: white;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: all var(--transition-normal);
  margin-top: 16px;
  box-shadow: var(--shadow-md);

  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-lg);
  }

  &:disabled {
    background: var(--disabled);
    cursor: not-allowed;
    transform: none;
  }
`;

const PaymentSteps = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 32px;
  color: var(--text-secondary);
  font-size: 14px;

  span {
    margin: 0 8px;
  }
`;

const Step = styled.div`
  color: ${props => props.active ? 'var(--primary)' : 'var(--text-secondary)'};
  font-weight: ${props => props.active ? '600' : '400'};
`;

const ConfirmationSection = styled.div`
  margin-top: 32px;
  padding: 24px;
  background: var(--surface);
  border: 1px solid var(--success);
  border-radius: 12px;
  box-shadow: var(--shadow-md);
`;

const ConfirmationDetails = styled.div`
  margin-bottom: 24px;

  h3 {
    font-size: 18px;
    margin-bottom: 16px;
    color: var(--text-primary);
  }

  p {
    display: flex;
    justify-content: space-between;
    margin: 8px 0;
    color: var(--text-secondary);
  }
`;

const PaymentDetails = ({ onClose, onSuccess }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    cardNumber: '',
    expiryDate: '',
    cvv: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    setIsProcessing(true);
    setError(null);

    try {
      // Validate payment details
      if (!formData.cardNumber || !formData.expiryDate || 
          !formData.cvv || !formData.name) {
        throw new Error('Please fill in all payment details');
      }

      // Show confirmation section
      setShowConfirmation(true);
    } catch (error) {
      setError(error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConfirmPayment = async () => {
    setIsProcessing(true);
    try {
      // Process payment here
      await new Promise(resolve => setTimeout(resolve, 2000)); // Simulated delay
      
      // Show success message and call success callback
      alert('Payment successful! Welcome to Pro!');
      onSuccess?.();
    } catch (error) {
      setError('Payment failed. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancel = () => {
    onClose?.();
  };

  return (
    <Layout>
      <Container>
        <PaymentSteps>
          <Step>Your details</Step>
          <span>→</span>
          <Step active>Payment</Step>
        </PaymentSteps>

        <OrderSummary>
          <div>
            <h2>Order summary</h2>
            <h3>${8.00}</h3>
            <p>then ${8.00} monthly starting {new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString()}</p>
            
            <div>
              <h4>Nexpend Pro</h4>
              <p>Nexpend Pro Monthly</p>
              <p>Qty: 1</p>
            </div>

            <div>
              <p>Subtotal</p>
              <p>${8.00}</p>
            </div>

            <div>
              <p>VAT</p>
              <p>${0.00}</p>
            </div>

            <div>
              <strong>Due today</strong>
              <strong>${8.00}</strong>
            </div>
          </div>
        </OrderSummary>

        <Form onSubmit={handlePaymentSubmit}>
          <PaymentOptions>
            

            <FormGroup>
              <Label>Card number</Label>
              <InputWrapper>
                <Input
                  type="text"
                  name="cardNumber"
                  placeholder="XXXX XXXX XXXX XXXX"
                  value={formData.cardNumber}
                  onChange={handleChange}
                  required
                  maxLength="19"
                />
                
              </InputWrapper>
            </FormGroup>

            <FormGroup>
              <Label>Name on card</Label>
              <Input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </FormGroup>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <FormGroup>
                <Label>Expiration date</Label>
                <Input
                  type="text"
                  name="expiryDate"
                  placeholder="MM / YY"
                  value={formData.expiryDate}
                  onChange={handleChange}
                  required
                  maxLength="5"
                />
              </FormGroup>

              <FormGroup>
                <Label>Security code</Label>
                <Input
                  type="text"
                  name="cvv"
                  placeholder="CVV"
                  value={formData.cvv}
                  onChange={handleChange}
                  required
                  maxLength="3"
                />
              </FormGroup>
            </div>
          </PaymentOptions>

          {error && <ErrorMessage>{error}</ErrorMessage>}

          {!showConfirmation ? (
            <SubscribeButton type="submit" disabled={isProcessing}>
              {isProcessing ? '🔄 Processing...' : 'Continue to confirmation'}
            </SubscribeButton>
          ) : (
            <ConfirmationSection>
              <ConfirmationDetails>
                <h3>🔒 Confirm your payment</h3>
                <p>
                  <span>Plan:</span>
                  <span>Nexpend Pro Monthly</span>
                </p>
                <p>
                  <span>Amount:</span>
                  <span>${8.00}/month</span>
                </p>
                <p>
                  <span>Card:</span>
                  <span>**** **** **** {formData.cardNumber.slice(-4)}</span>
                </p>
                <p>
                  <span>Name:</span>
                  <span>{formData.name}</span>
                </p>
                <p>
                  <span>Next billing date:</span>
                  <span>{new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString()}</span>
                </p>
              </ConfirmationDetails>

              <ButtonGroup>
                <CancelButton 
                  type="button" 
                  onClick={() => setShowConfirmation(false)}
                >
                  Back
                </CancelButton>
                <SubscribeButton
                  type="button"
                  onClick={handleConfirmPayment}
                  disabled={isProcessing}
                >
                  {isProcessing ? '🔄 Processing payment...' : 'Confirm payment'}
                </SubscribeButton>
              </ButtonGroup>
            </ConfirmationSection>
          )}
        </Form>
      </Container>
    </Layout>
  );
};

export default PaymentDetails; 